import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef, useState } from 'react';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// תיקון סטנדרטי - webpack/CRA לא פותר נכון את נתיבי אייקון ברירת המחדל של
// Leaflet (מוגדרים כמחרוזות יחסיות בקוד הספרייה עצמה), כך שבלי זה הסיכה
// לא מוצגת בכלל בבאנדל מבונה. מייבאים את קבצי התמונה ישירות (webpack
// מחזיר להם URL מתאים) וקושרים אותם ל-Icon.Default שכל Marker משתמש בו
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const ISRAEL_CENTER = [31.7683, 35.2137];

// בורר מיקום על גבי מפה (OpenStreetMap/Leaflet - לא דורש מפתח API, בניגוד
// ל-Google Maps) - שדה חיפוש כתובת (Nominatim, שירות גיאוקודינג חינמי של
// OSM) שממקם את המפה + הסיכה, ולחיצה/גרירה ישירה על המפה לבחירת נקודה
// מדויקת. לא עוטף ב-react-leaflet (כדי להימנע מהתאמות גרסת React) - Leaflet
// מנוהל ישירות באופן אימפרטיבי דרך useRef/useEffect, כמו בכל אינטגרציה
// לא-React אחרת (jQuery plugins וכו')
export default function MapPicker({ lat, lng, onChange }) {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    const initialCenter = (lat != null && lng != null) ? [lat, lng] : ISRAEL_CENTER;
    const map = L.map(mapElRef.current).setView(initialCenter, (lat != null && lng != null) ? 15 : 7);
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    if (lat != null && lng != null) {
      markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current.getLatLng();
        onChange(pos.lat, pos.lng);
      });
    }

    map.on('click', (e) => {
      placeMarker(e.latlng.lat, e.latlng.lng);
      onChange(e.latlng.lat, e.latlng.lng);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // נבנה פעם אחת בלבד בעת העלאת הרכיב - שינויי lat/lng חיצוניים (למשל
    // מהחיפוש עצמו) מטופלים ידנית ע"י placeMarker, לא ע"י re-init של המפה
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // מסנכרן שינוי חיצוני של lat/lng (עריכה ידנית בשדות המספריים ליד המפה)
  // אל תוך המפה - בלי לאתחל אותה מחדש. משווה למיקום הנוכחי של הסיכה כדי
  // לא ליצור לולאה עם onChange שהמפה עצמה מפעילה בלחיצה/גרירה
  useEffect(() => {
    if (lat == null || lng == null || !mapRef.current) return;
    const current = markerRef.current?.getLatLng();
    if (current && Math.abs(current.lat - lat) < 1e-7 && Math.abs(current.lng - lng) < 1e-7) return;
    placeMarker(lat, lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  const placeMarker = (newLat, newLng, zoom) => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([newLat, newLng]);
    } else {
      markerRef.current = L.marker([newLat, newLng], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current.getLatLng();
        onChange(pos.lat, pos.lng);
      });
    }
    map.setView([newLat, newLng], zoom || Math.max(map.getZoom(), 15));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setSearchError('');
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&accept-language=he&countrycodes=il&q=${encodeURIComponent(search.trim())}`;
      const resp = await fetch(url);
      const results = await resp.json();
      if (!results?.length) {
        setSearchError('הכתובת לא נמצאה');
        return;
      }
      const { lat: foundLat, lon: foundLng } = results[0];
      placeMarker(parseFloat(foundLat), parseFloat(foundLng));
      onChange(parseFloat(foundLat), parseFloat(foundLng));
    } catch {
      setSearchError('שגיאה בחיפוש הכתובת');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSearch} style={s.searchRow}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש כתובת..."
          style={s.searchInput}
        />
        <button type="submit" style={s.searchBtn} disabled={searching}>
          {searching ? 'מחפש...' : 'חיפוש'}
        </button>
      </form>
      {searchError && <p style={s.searchError}>{searchError}</p>}
      <p style={s.hint}>אפשר גם ללחוץ ישירות על המפה, או לגרור את הסיכה, כדי לקבוע את הנקודה המדויקת.</p>
      <div ref={mapElRef} style={s.mapBox} />
    </div>
  );
}

const s = {
  searchRow: { display: 'flex', gap: 8, marginBottom: 6 },
  searchInput: { flex: 1, padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13, boxSizing: 'border-box', direction: 'rtl' },
  searchBtn: { padding: '9px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  searchError: { color: '#c62828', fontSize: 12, margin: '2px 0 6px' },
  hint: { color: '#8792a2', fontSize: 11.5, margin: '0 0 8px' },
  mapBox: { width: '100%', height: 280, borderRadius: 12, overflow: 'hidden', border: '1px solid #e3e8ee' },
};
